import { esFemenino } from '../motor/genero'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Eye, Palette, XCircle } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import type { ErrorDeLaApi } from '../api/nucleo'
import {
  type ColeccionPublicable,
  type ContenidoPendiente,
  type Pendiente,
  type PublicacionDeLaWeb,
  previaDe,
  publicarLaWeb,
  type ResultadoDelDiseno,
  usePendiente,
  useRefrescarPublicacion,
} from '../api/publicacion'
import { Aviso, Boton, Cargando, Casilla, Modal, Pastilla, clases } from '../componentes/ui'
import { direccionDeLaWeb } from '../marco/secciones'
import {
  alternar,
  alternarGrupo,
  avisosDe,
  COLECCIONES,
  cuantos,
  detalleDe,
  documentosDelPedido,
  ESTRUCTURA,
  esDeEstructura,
  disenoDisponible,
  elementosDe,
  enlaceDe,
  estadoDelGrupo,
  grupoDe,
  juntar,
  MOTIVO_SIN_PORTADA,
  NOMBRES,
  portadaFallida,
  resumen,
  saleSinPortada,
  type Seleccion,
  seleccionInicial,
  tandas,
  textoDeConfirmacion,
  tituloDe,
} from './plan'

/**
 * «Publicar mi web» (CLAUDE.md §13, §20): lo que la web tiene pendiente —páginas,
 * productos, servicios y entradas en borrador o con cambios sin publicar, y el diseño—, cada cosa con
 * su casilla, y un solo botón. Lo abren la cabecera del panel, el final del alta y la
 * pantalla de Diseño: es el mismo componente, y la misma regla en los tres.
 *
 * Lo que se publica es **lo que se enseña**, por id, y en el orden que importa: el
 * contenido primero, el diseño en la última tanda (con la versión que se ha visto). Qué se
 * puede lo vuelve a decidir el núcleo en cada llamada; aquí se pinta lo que responde, con
 * sus palabras (§28). Antes de publicar se confirma: publicar es un acto humano (§27).
 */
type Fase = 'confirmando' | 'eligiendo' | 'hecho' | 'publicando'

type Props = {
  espacio: number
  sitio: number
  cerrar: () => void
  /** Desde Diseño: la versión que se está mirando en la previa. Sin ella, la última lista. */
  version?: null | number
  /** Si el diseño sale marcado de partida (si se puede). */
  conDiseno?: boolean
  /** Si el contenido sale marcado de partida. */
  conContenido?: boolean
  /** Por qué se abre desde aquí: una línea encima de todo. */
  explicacion?: ReactNode
  /** Al terminar (se haya publicado todo o no): para refrescar la previa, o decirlo en la pantalla de fondo. */
  alTerminar?: (r: PublicacionDeLaWeb) => void
}

const CONSTRUIDA = ['lista', 'publicada', 'archivada']

export const DialogoDePublicacion = (p: Props) => {
  const { data: pendiente, error, isPending } = usePendiente(p.sitio)
  const [fase, setFase] = useState<Fase>('eligiendo')
  const ocupado = fase === 'publicando'
  const cerrar = () => {
    if (!ocupado) p.cerrar()
  }
  return (
    <Modal ancho="max-w-2xl" cerrar={cerrar} titulo="Publicar mi web">
      {error ? (
        <Aviso tono="error">{(error as ErrorDeLaApi).message}</Aviso>
      ) : isPending || !pendiente ? (
        <Cargando que="Mirando qué queda por publicar" />
      ) : (
        <Contenido {...p} cerrar={cerrar} fase={fase} pendiente={pendiente} setFase={setFase} />
      )}
    </Modal>
  )
}

/**
 * Al cambiar de paso, el foco va a lo que toca: «Sí, publicar» al confirmar, el resumen al
 * terminar. Quien navega con teclado o con lector de pantalla no se queda en un botón que
 * ya no existe.
 */
const Contenido = (props: Props & { fase: Fase; pendiente: Pendiente; setFase: (f: Fase) => void }) => {
  const raiz = useRef<HTMLDivElement>(null)
  useEffect(() => {
    raiz.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
  }, [props.fase])
  return (
    <div ref={raiz}>
      <Pasos {...props} />
    </div>
  )
}

const Pasos = ({ pendiente: pend, fase, setFase, ...p }: Props & { fase: Fase; pendiente: Pendiente; setFase: (f: Fase) => void }) => {
  // La selección nace con lo que se enseña la primera vez y no se rehace aunque se refresque lo pendiente.
  const [seleccion, setSeleccion] = useState<Seleccion>(() => {
    const inicial = seleccionInicial(pend, { diseno: p.conDiseno ?? true, version: p.version })
    return p.conContenido === false ? { ...inicial, paginas: new Set(), productos: new Set(), servicios: new Set(), entradas: new Set() } : inicial
  })
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0 })
  const [resultado, setResultado] = useState<null | PublicacionDeLaWeb>(null)
  const [fallo, setFallo] = useState<null | string>(null)
  const refrescar = useRefrescarPublicacion()
  // Se congela lo que se enseñó al abrir: lo que se publica es exactamente eso.
  const [pendiente] = useState(pend)

  const n = cuantos(seleccion)
  const versionDelDiseno = p.version ?? pendiente.diseno.version?.numero ?? null
  const hayAlgo = COLECCIONES.some((c) => elementosDe(pendiente, c).length > 0) || disenoDisponible(pendiente.diseno, p.version)

  const publicar = async () => {
    setFase('publicando')
    setFallo(null)
    const lista = tandas(pendiente, seleccion, { version: p.version })
    const total = lista.reduce((suma, t) => suma + documentosDelPedido(t) + (t.diseno ? 1 : 0), 0)
    setProgreso({ hechos: 0, total })
    const resultados: PublicacionDeLaWeb[] = []
    let disenoSinPortada: null | ResultadoDelDiseno = null
    let hechos = 0
    for (const tanda of lista) {
      let pedido = tanda
      // La portada se pidió y no ha salido: el diseño no se manda detrás (la web abriría en «Esta página no existe»).
      if (tanda.diseno && portadaFallida(pendiente, resultados)) {
        pedido = { ...tanda, diseno: false }
        disenoSinPortada = { accion: 'ninguna', version: null, motivo: MOTIVO_SIN_PORTADA }
      }
      // Todas las listas: una tanda con solo la estructura o los contenidos de un tipo también se manda.
      const cuantosVan = documentosDelPedido(pedido)
      if (cuantosVan > 0 || pedido.diseno) {
        try {
          resultados.push(await publicarLaWeb(p.sitio, pedido))
        } catch (e) {
          setFallo(`${(e as ErrorDeLaApi).message || 'No se ha podido publicar.'} Lo de antes sí se ha publicado; vuelve a abrir esto para seguir con lo que queda.`)
          break
        }
      }
      hechos += cuantosVan + (tanda.diseno ? 1 : 0)
      setProgreso({ hechos, total })
    }
    const final = juntar(resultados)
    if (disenoSinPortada) final.diseno = disenoSinPortada
    setResultado(final)
    setFase('hecho')
    refrescar(p.sitio)
    p.alTerminar?.(final)
  }

  if (fase === 'hecho' && resultado) return <Hecho {...p} fallo={fallo} pendiente={pendiente} resultado={resultado} versionDelDiseno={versionDelDiseno} />

  if (fase === 'publicando') {
    const porciento = progreso.total > 0 ? Math.round((progreso.hechos / progreso.total) * 100) : 0
    return (
      <div className="flex flex-col gap-3 py-4" role="status">
        <p className="text-sm">
          Publicando… {progreso.hechos} de {progreso.total}
        </p>
        <div aria-label="Progreso de la publicación" aria-valuemax={100} aria-valuemin={0} aria-valuenow={porciento} className="h-2 overflow-hidden rounded-pastilla bg-papel-3" role="progressbar">
          <div className="h-full bg-violeta transition-all" style={{ width: `${porciento}%` }} />
        </div>
        <p className="text-xs text-tenue">El contenido primero y el diseño al final. No cierres esta ventana hasta que termine.</p>
      </div>
    )
  }

  if (!hayAlgo) {
    return (
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex items-start gap-3">
          <CheckCircle2 aria-hidden className="mt-0.5 size-5 shrink-0 text-ok" />
          <div>
            <p className="font-medium">Tu web está al día</p>
            <p className="mt-1 text-tenue">No queda nada por publicar.{pendiente.diseno.situacion !== 'al-dia' ? ` Del diseño: ${pendiente.diseno.motivo ?? pendiente.diseno.texto}` : ''}</p>
          </div>
        </div>
        <Pie>
          <Boton onClick={p.cerrar}>Cerrar</Boton>
          {pendiente.enInternet && <VerMiWeb dominio={pendiente.dominio} />}
        </Pie>
      </div>
    )
  }

  if (fase === 'confirmando') {
    return (
      <div className="flex flex-col gap-4 text-sm">
        <p>{textoDeConfirmacion(pendiente, seleccion)}</p>
        {saleSinPortada(pendiente, seleccion) && <Aviso>Tu portada no está marcada: quien visite tu web verá «Esta página no existe» hasta que la publiques.</Aviso>}
        <Pie>
          <Boton onClick={() => setFase('eligiendo')}>Volver</Boton>
          <Boton data-autofocus onClick={() => void publicar()} tipo="primario">
            Sí, publicar
          </Boton>
        </Pie>
      </div>
    )
  }

  const porRellenarPublicadas = !pendiente.enInternet && seleccion.diseno ? (pendiente.paginas?.publicadasPorRellenar ?? []) : []
  // Ni publicada ni entre lo pendiente: no hay portada que publicar, hay que crearla.
  const sinPaginaDeInicio = Boolean(pendiente.paginas && !pendiente.paginas.inicioPublicado && !pendiente.paginas.borradores.some((b) => b.portada) && pendiente.paginas.fuera === 0)
  const hayEstructura = ESTRUCTURA.some((c) => elementosDe(pendiente, c).length > 0)
  const primerContenido = COLECCIONES.find((c) => !esDeEstructura(c) && elementosDe(pendiente, c).length > 0)
  return (
    <div className="flex flex-col gap-4 text-sm">
      {p.explicacion && <p className="text-tinta-2">{p.explicacion}</p>}
      <p className="text-tenue">Marca lo que quieres publicar. Lo que tiene un aviso se puede publicar igual, pero saldrá así en tu web.</p>
      <div className="flex flex-col gap-2">
        {/* La estructura (tipos, taxonomías y grupos de campos) va junta y arriba: se publica antes que el contenido. */}
        {hayEstructura && <p className="mt-1 text-xs font-medium tracking-wide text-tenue uppercase">Estructura: tipos, taxonomías y grupos de campos</p>}
        {COLECCIONES.map((c) =>
          elementosDe(pendiente, c).length > 0 ? (
            <GrupoConRotulo antes={hayEstructura && c === primerContenido} key={c}>
              <GrupoDeDocumentos coleccion={c} espacio={p.espacio} pendiente={pendiente} seleccion={seleccion} setSeleccion={setSeleccion} sitio={p.sitio} />
            </GrupoConRotulo>
          ) : null,
        )}
        <FilaDelDiseno espacio={p.espacio} pendiente={pendiente} seleccion={seleccion} setSeleccion={setSeleccion} sitio={p.sitio} version={p.version ?? null} />
      </div>
      {sinPaginaDeInicio ? (
        <Aviso>
          Tu web no tiene página de inicio: créala en Páginas con la dirección «inicio», sin página padre. Sin ella, tu web y la previa abren en «Esta página no existe».{' '}
          <Link className="font-medium underline" onClick={p.cerrar} to={`/e/${p.espacio}/s/${p.sitio}/paginas`}>
            Ir a Páginas
          </Link>
        </Aviso>
      ) : (
        saleSinPortada(pendiente, seleccion) && <Aviso>Tu portada no está marcada: si publicas el diseño, quien visite tu web verá «Esta página no existe» hasta que la publiques.</Aviso>
      )}
      {porRellenarPublicadas.length > 0 && (
        <Aviso>
          {porRellenarPublicadas.length === 1 ? 'Una página ya publicada tiene' : `${porRellenarPublicadas.length} páginas ya publicadas tienen`} textos «por rellenar»: con el diseño, saldrán así en internet ({porRellenarPublicadas.map((x) => `«${x.titulo}»`).join(', ')}).
        </Aviso>
      )}
      <Pie>
        <Boton onClick={p.cerrar}>Cancelar</Boton>
        <Boton disabled={n === 0} onClick={() => setFase('confirmando')} tipo="primario">
          {pendiente.diseno.puede === 'solicitar' && seleccion.diseno && n === 1 ? 'Solicitar la publicación' : `Publicar (${n})`}
        </Boton>
      </Pie>
    </div>
  )
}

/**
 * Tras el último grupo de la estructura, un rótulo para el contenido: así se ve dónde acaba lo
 * que define la web y empieza lo que se publica en ella.
 */
const GrupoConRotulo = ({ antes, children }: { antes: boolean; children: ReactNode }) => (
  <>
    {antes && <p className="mt-2 text-xs font-medium tracking-wide text-tenue uppercase">Contenido</p>}
    {children}
  </>
)

/** Los botones, siempre a la vista al fondo del diálogo aunque la lista sea larga (en un móvil, sobre todo). */
const Pie = ({ children }: { children: ReactNode }) => <div className="sticky -bottom-5 -mx-5 -mb-5 mt-1 flex flex-wrap justify-end gap-2 border-t border-linea bg-papel px-5 py-3.5">{children}</div>

const GrupoDeDocumentos = ({
  coleccion,
  pendiente,
  seleccion,
  setSeleccion,
  espacio,
  sitio,
}: {
  coleccion: ColeccionPublicable
  espacio: number
  pendiente: Pendiente
  seleccion: Seleccion
  setSeleccion: (s: Seleccion) => void
  sitio: number
}) => {
  const elementos = elementosDe(pendiente, coleccion) as Array<{ id: number; cambios?: boolean }>
  const ids = elementos.map((e) => e.id)
  const estado = estadoDelGrupo(seleccion, coleccion, ids)
  const conAvisos = elementos.filter((e) => avisosDe(coleccion, e).length > 0).length
  // Abierto de partida si es corto o si algo lleva aviso: lo que va a salir raro se ve antes de pulsar.
  const [abierto, setAbierto] = useState(elementos.length <= 6 || conAvisos > 0)
  const idDeLista = useId()
  const nombre = NOMBRES[coleccion]
  const fuera = grupoDe(pendiente, coleccion)?.fuera ?? 0
  return (
    <section className="rounded-caja border border-linea">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Casilla aMedias={estado === 'algunos'} etiqueta={`Marcar todo en ${nombre.titulo}`} marcada={estado === 'todos'} onChange={() => setSeleccion(alternarGrupo(seleccion, coleccion, ids))} />
        <button aria-controls={idDeLista} aria-expanded={abierto} className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setAbierto(!abierto)} type="button">
          <span className="font-medium">{nombre.titulo}</span>
          <span className="text-tenue tabular-nums">
            {seleccion[coleccion].size} de {elementos.length}
          </span>
          {conAvisos > 0 && (
            <Pastilla clave="construyendo" punto={false}>
              {conAvisos} con avisos
            </Pastilla>
          )}
          <span className="ml-auto text-tenue">{abierto ? <ChevronDown aria-hidden className="size-4" /> : <ChevronRight aria-hidden className="size-4" />}</span>
        </button>
      </div>
      {abierto && (
        <ul aria-label={nombre.titulo} className="flex max-h-72 flex-col divide-y divide-linea overflow-y-auto border-t border-linea" id={idDeLista}>
          {elementos.map((e) => {
            const titulo = tituloDe(coleccion, e)
            return (
              <li className="flex items-center gap-3 px-3 py-2 text-[13px]" key={e.id}>
                <Casilla etiqueta={titulo} marcada={seleccion[coleccion].has(e.id)} onChange={() => setSeleccion(alternar(seleccion, coleccion, e.id))} />
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                  <span className="truncate">{titulo}</span>
                  {detalleDe(coleccion, e) && <span className="text-tenue">· {detalleDe(coleccion, e)}</span>}
                  {avisosDe(coleccion, e).map((a) => (
                    <Pastilla clave={a === 'cambios sin publicar' ? 'neutra' : 'construyendo'} key={a} punto={false}>
                      {a}
                    </Pastilla>
                  ))}
                </span>
                <Link className="shrink-0 font-medium text-violeta hover:underline" to={enlaceDe(espacio, sitio, coleccion, e.id, coleccion === 'contenidos' ? (e as ContenidoPendiente).tipoId : null)}>
                  Editar
                </Link>
              </li>
            )
          })}
        </ul>
      )}
      {fuera > 0 && (
        <p className="border-t border-linea px-3 py-2 text-xs text-tenue">
          {fuera === 1 ? `Hay ${esFemenino(nombre.singular) ? 'otra' : 'otro'} más` : `Hay ${fuera} más`} que no entran en esta lista: se publican en otra vuelta, o desde {nombre.titulo}.
        </p>
      )}
    </section>
  )
}

const FilaDelDiseno = ({
  pendiente,
  seleccion,
  setSeleccion,
  espacio,
  sitio,
  version,
}: {
  espacio: number
  pendiente: Pendiente
  seleccion: Seleccion
  setSeleccion: (s: Seleccion) => void
  sitio: number
  version: null | number
}) => {
  const d = pendiente.diseno
  const disponible = disenoDisponible(d, version)
  const numero = version ?? d.version?.numero ?? null
  const etiqueta = d.puede === 'solicitar' ? `Solicitar la publicación del diseño${numero ? ` (v${numero})` : ''}` : `Publicar el diseño${numero ? ` (v${numero})` : ''}`
  // Sin nada que hacer con el diseño y al día, no hace falta ni la fila.
  if (!disponible && d.situacion === 'al-dia') return null
  const explicacion = disponible
    ? d.puede === 'solicitar'
      ? 'En tu plan lo revisa el equipo antes de publicarlo: se le manda la solicitud.'
      : d.publicada === 0
        ? 'Es lo que saca tu web a internet: se publica después del contenido.'
        : 'Se publica después del contenido.'
    : (d.motivo ?? d.texto)
  return (
    <section className={clases('flex items-start gap-3 rounded-caja border px-3 py-2.5', disponible ? 'border-linea' : 'border-linea bg-papel-2')}>
      <Casilla className="mt-0.5" disabled={!disponible} etiqueta={etiqueta} marcada={disponible && seleccion.diseno} onChange={() => setSeleccion({ ...seleccion, diseno: !seleccion.diseno })} />
      <div className="min-w-0 flex-1">
        <p className={clases('flex items-center gap-2 font-medium', !disponible && 'text-tenue')}>
          <Palette aria-hidden className="size-4 text-violeta" />
          {disponible ? etiqueta : 'El diseño'}
        </p>
        <p className="mt-0.5 text-[13px] text-tenue">
          {explicacion}{' '}
          {!disponible && (
            <Link className="font-medium text-violeta hover:underline" to={`/e/${espacio}/s/${sitio}/diseno`}>
              Ir a Diseño
            </Link>
          )}
        </p>
      </div>
    </section>
  )
}

const VerMiWeb = ({ dominio }: { dominio: string }) => {
  const url = direccionDeLaWeb(dominio)
  if (!url) return null
  return (
    <a className="inline-flex h-9 items-center gap-1.5 rounded-boton bg-violeta px-3.5 text-sm font-medium text-white shadow-suave hover:bg-violeta-2" href={url} rel="noopener noreferrer" target="_blank">
      <ExternalLink aria-hidden className="size-4" />
      Ver mi web
    </a>
  )
}

const Hecho = ({
  resultado,
  pendiente,
  fallo,
  espacio,
  sitio,
  cerrar,
  version,
  versionDelDiseno,
}: Props & { fallo: null | string; pendiente: Pendiente; resultado: PublicacionDeLaWeb; versionDelDiseno: null | number }) => {
  const r = resumen(resultado)
  const enInternet = pendiente.enInternet || resultado.diseno?.accion === 'publicado'
  // Sin la web en internet, lo publicado se mira en la previa. El enlace se pide ya, para que
  // abrirlo sea un clic de verdad: Safari no abre una pestaña después de un `await`. La versión
  // que llega de Diseño está construida (es la de la previa); la última, si su estado lo dice.
  const construida = version != null || CONSTRUIDA.includes(pendiente.diseno.version?.estado ?? '')
  const versionParaLaPrevia = !enInternet && versionDelDiseno !== null && construida ? versionDelDiseno : null
  const { data: previa } = useQuery({ queryKey: ['previa', sitio, versionParaLaPrevia], queryFn: () => previaDe(sitio, versionParaLaPrevia!), enabled: versionParaLaPrevia !== null, retry: false, staleTime: 5 * 60_000 })
  const fallidos = COLECCIONES.flatMap((c) => resultado[c].fallidos.map((f) => ({ ...f, coleccion: c })))
  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className="outline-none" data-autofocus tabIndex={-1}>
        <Aviso tono={r.tono}>{r.texto}</Aviso>
      </div>
      {fallo && <Aviso tono="error">{fallo}</Aviso>}
      {resultado.diseno && resultado.diseno.accion !== 'ninguna' && (
        <p className="flex items-center gap-2">
          <CheckCircle2 aria-hidden className="size-4 text-ok" />
          {resultado.diseno.accion === 'publicado' ? `Diseño publicado: tu web ya está en internet con la v${resultado.diseno.version}.` : `Solicitud enviada: el equipo revisa la v${resultado.diseno.version} y te avisamos cuando esté en línea.`}
        </p>
      )}
      {resultado.diseno?.accion === 'ninguna' && resultado.diseno.motivo && (
        <div className="flex items-start gap-2 rounded-caja border border-error/30 bg-error-suave px-3 py-2 text-[13px]">
          <XCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-error" />
          <p>
            <span className="font-medium">El diseño no se ha publicado.</span> {resultado.diseno.motivo}{' '}
            <Link className="font-medium text-violeta hover:underline" onClick={cerrar} to={`/e/${espacio}/s/${sitio}/diseno`}>
              Ir a Diseño
            </Link>
          </p>
        </div>
      )}
      {fallidos.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-medium">Lo que no se ha podido publicar, y por qué</p>
          <ul aria-label="Lo que no se ha podido publicar" className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {fallidos.map((f) => (
              <li className="rounded-caja border border-error/30 bg-error-suave px-3 py-2 text-[13px]" key={`${f.coleccion}-${f.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 font-medium">
                    <XCircle aria-hidden className="size-4 shrink-0 text-error" />
                    <span className="truncate">{f.titulo}</span>
                  </span>
                  <Link className="shrink-0 font-medium text-violeta hover:underline" onClick={cerrar} to={enlaceDe(espacio, sitio, f.coleccion, f.id)}>
                    Editar
                  </Link>
                </div>
                <p className="mt-1 text-tinta-2">{f.motivo}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!enInternet && <p className="text-tenue">Tu web todavía no está en internet: sale cuando se publique el diseño. Lo publicado ya se ve en la previa.</p>}
      <Pie>
        <Boton onClick={cerrar}>Cerrar</Boton>
        {enInternet ? (
          <VerMiWeb dominio={pendiente.dominio} />
        ) : previa ? (
          <a className="inline-flex h-9 items-center gap-1.5 rounded-boton border border-linea bg-papel px-3.5 text-sm font-medium shadow-suave hover:bg-papel-2" href={previa.url} rel="noopener noreferrer" target="_blank">
            <Eye aria-hidden className="size-4" />
            Ver la previa
          </a>
        ) : null}
      </Pie>
    </div>
  )
}
